import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { ApiClient } from '@/api/client'
import { useApiClient } from '@/api/ApiClientProvider'
import { drillDownHref, type DrillDown } from '@/lib/drilldown'
import type { GlobalFilters } from '@/lib/filters'
import { invalidateInsights } from '@/lib/insights/queries'
import { filtersKey, useGlobalFilters } from '@/lib/useGlobalFilters'

/** One Insights page's data, keyed by the filters in the URL — the Overview's `useOverviewQuery`, for this feature. */
export function useInsightsQuery<T>(
  name: string,
  fetch: (client: ApiClient, filters: GlobalFilters) => Promise<T>,
  extraKey: readonly unknown[] = [],
) {
  const client = useApiClient()
  const { filters } = useGlobalFilters()
  return useQuery({
    queryKey: ['insights', name, client.kind, filtersKey(filters), ...extraKey],
    queryFn: () => fetch(client, filters),
    placeholderData: keepPreviousData,
  })
}

/** Builds a drill-down link that carries the current filters. */
export function useDrillDown() {
  const { filters } = useGlobalFilters()
  return (target: DrillDown) => drillDownHref(target, filters)
}

/**
 * A mutation that acts on one insight by id (acknowledge, dismiss) and
 * refetches every Insights query once it settles, so the Overview's alert count
 * and every Insights page agree the moment one of them changes state — and an
 * alert that resolved meanwhile (a 409) leaves the list rather than lingering.
 * The mutation stays pending until that refetch lands, so its buttons stay
 * disabled until the list shows the new state.
 */
export function useInsightAction(action: (client: ApiClient, id: string) => Promise<void>) {
  const client = useApiClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => action(client, id),
    onSettled: () => invalidateInsights(queryClient),
  })
}
